/**
 * MIA Upload Service
 * Singleton service to manage background MIA AI uploads
 * Matches Flutter's MiaUploadService exactly
 */

import { reactive, readonly } from "vue";
import { apiUploadFile } from "./api";
import { miaRefreshService } from "./refreshService";
import {
  MiaFeatureType,
  MiaUploadStatus,
  MiaRefreshType,
  getFeatureEndpoint,
} from "../types";

/**
 * Upload task class - matches Flutter's MiaUploadTask
 */
class MiaUploadTask {
  constructor({
    id,
    file,
    fileName,
    featureType,
    status = MiaUploadStatus.PENDING,
    uploadedBytes = 0,
    totalBytes = 0,
    resultId = null,
    solutionId = null,
    errorMessage = null,
    extraData = null,
  }) {
    this.id = id;
    this.file = file;
    this.fileName = fileName;
    this.featureType = featureType;
    this.status = status;
    this.uploadedBytes = uploadedBytes;
    this.totalBytes = totalBytes;
    this.resultId = resultId;
    this.solutionId = solutionId;
    this.errorMessage = errorMessage;
    this.extraData = extraData;
    this.isCancelled = false;
    this.abortController = null;
  }

  get progress() {
    return this.totalBytes > 0 ? this.uploadedBytes / this.totalBytes : 0;
  }

  copyWith(updates) {
    const task = new MiaUploadTask({
      id: this.id,
      file: this.file,
      fileName: this.fileName,
      featureType: this.featureType,
      status: updates.status ?? this.status,
      uploadedBytes: updates.uploadedBytes ?? this.uploadedBytes,
      totalBytes: updates.totalBytes ?? this.totalBytes,
      resultId: updates.resultId ?? this.resultId,
      solutionId: updates.solutionId ?? this.solutionId,
      errorMessage: updates.errorMessage ?? this.errorMessage,
      extraData: this.extraData,
    });
    task.isCancelled = this.isCancelled;
    task.abortController = this.abortController;
    return task;
  }
}

/**
 * Upload service state
 */
const state = reactive({
  uploads: {}, // Map<string, MiaUploadTask>
  listeners: [],
});

/**
 * Notify all listeners of state change
 */
function notifyListeners() {
  state.listeners.forEach((listener) => {
    try {
      listener();
    } catch (e) {
      console.error("[MiaUploadService] Listener error:", e);
    }
  });
}

/**
 * Get endpoint for feature type - matches Flutter's _getEndpoint
 * Note: apiUrl already includes '/api', so endpoints should NOT include 'api/' prefix
 */
function getEndpoint(featureType) {
  switch (featureType) {
    case MiaFeatureType.SUMMARY:
      return "ai/process-content";
    case MiaFeatureType.FLASH_CARDS:
      return "flash-cards/decks";
    case MiaFeatureType.QBANK:
      return "question-banks/from-content";
    case MiaFeatureType.PROBLEM_SOLVING:
      return "ai/problems";
    default:
      throw new Error(`Unknown feature type: ${featureType}`);
  }
}

/**
 * Convert MiaFeatureType to MiaRefreshType
 */
function toRefreshType(featureType) {
  switch (featureType) {
    case MiaFeatureType.SUMMARY:
      return MiaRefreshType.SUMMARY;
    case MiaFeatureType.FLASH_CARDS:
      return MiaRefreshType.FLASH_CARDS;
    case MiaFeatureType.QBANK:
      return MiaRefreshType.QBANK;
    case MiaFeatureType.PROBLEM_SOLVING:
      return MiaRefreshType.PROBLEM_SOLVING;
    default:
      return featureType;
  }
}

/**
 * Perform the actual upload - matches Flutter's _performUpload
 */
async function performUpload(id) {
  const task = state.uploads[id];
  if (!task) return;

  // Update status to uploading
  state.uploads[id] = task.copyWith({ status: MiaUploadStatus.UPLOADING });
  notifyListeners();

  // Check if cancelled before starting
  if (state.uploads[id]?.isCancelled) {
    state.uploads[id] = state.uploads[id].copyWith({
      status: MiaUploadStatus.CANCELLED,
      errorMessage: "Upload cancelled",
    });
    notifyListeners();
    return;
  }

  // Create abort controller for cancellation
  const abortController = new AbortController();
  state.uploads[id].abortController = abortController;

  try {
    const endpoint = getEndpoint(task.featureType);

    const response = await apiUploadFile(
      endpoint,
      task.file,
      task.extraData || {},
      (sent, total) => {
        // Check for cancellation during upload
        if (state.uploads[id]?.isCancelled) {
          abortController.abort();
          return;
        }
        if (state.uploads[id]) {
          state.uploads[id] = state.uploads[id].copyWith({
            uploadedBytes: sent,
            totalBytes: total,
          });
          notifyListeners();
        }
      },
      abortController.signal
    );

    if (response.error) {
      if (response.error.code === "CANCELLED") {
        state.uploads[id] = state.uploads[id].copyWith({
          status: MiaUploadStatus.CANCELLED,
          errorMessage: "Upload cancelled",
        });
      } else {
        state.uploads[id] = state.uploads[id].copyWith({
          status: MiaUploadStatus.FAILED,
          errorMessage: response.error.message || "Upload failed",
        });
      }
      notifyListeners();
      console.error(
        "[MiaUploadService] ❌ Upload failed:",
        response.error.message
      );
      return;
    }

    // Extract result ID from response based on feature type
    const data = response.data;
    let resultId = null;
    let solutionId = null;

    if (data) {
      resultId =
        data.upload_id ||
        data.id ||
        data.deck_id ||
        data.bank_id ||
        data.problem_id ||
        data.deckId ||
        data.bankId ||
        data.problemId ||
        null;
      solutionId = data.solution_id || data.solutionId || null;
    }

    // Upload completed successfully
    state.uploads[id] = state.uploads[id].copyWith({
      status: MiaUploadStatus.COMPLETED,
      resultId,
      solutionId,
    });
    notifyListeners();

    // Trigger refresh for the feature list
    miaRefreshService.refresh(toRefreshType(task.featureType));

    // Show browser notification if supported
    showUploadCompleteNotification(task.fileName, task.featureType);

    console.log(
      `[MiaUploadService] ✅ Upload completed: ${task.fileName}, resultId: ${resultId}`
    );
  } catch (e) {
    state.uploads[id] = state.uploads[id].copyWith({
      status: MiaUploadStatus.FAILED,
      errorMessage: e.message || "Upload error",
    });
    notifyListeners();
    console.error("[MiaUploadService] ❌ Upload error:", e);
  }
}

/**
 * Show browser notification for upload complete
 */
function showUploadCompleteNotification(fileName, featureType) {
  if (!("Notification" in window)) return;

  if (Notification.permission === "granted") {
    const title = getFeatureTitle(featureType);
    new Notification("Upload Complete", {
      body: `${title}: ${fileName}`,
      icon: "/favicon.ico",
    });
  } else if (Notification.permission !== "denied") {
    Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        const title = getFeatureTitle(featureType);
        new Notification("Upload Complete", {
          body: `${title}: ${fileName}`,
          icon: "/favicon.ico",
        });
      }
    });
  }
}

/**
 * Get feature title for notifications
 */
function getFeatureTitle(featureType) {
  switch (featureType) {
    case MiaFeatureType.SUMMARY:
      return "File Summaries";
    case MiaFeatureType.FLASH_CARDS:
      return "Flash Cards";
    case MiaFeatureType.QBANK:
      return "Question Bank";
    case MiaFeatureType.PROBLEM_SOLVING:
      return "Problem Solving";
    default:
      return "MIA AI";
  }
}

/**
 * MIA Upload Service - Singleton
 * Matches Flutter's MiaUploadService API
 */
export const miaUploadService = {
  /**
   * Get all current uploads
   * @returns {MiaUploadTask[]}
   */
  get uploads() {
    return Object.values(state.uploads);
  },

  /**
   * Get uploads for a specific feature type
   * @param {string} featureType
   * @returns {MiaUploadTask[]}
   */
  uploadsForFeature(featureType) {
    return Object.values(state.uploads).filter(
      (u) => u.featureType === featureType
    );
  },

  /**
   * Get uploads that are in progress
   * @returns {MiaUploadTask[]}
   */
  get activeUploads() {
    return Object.values(state.uploads).filter(
      (u) => u.status === MiaUploadStatus.UPLOADING
    );
  },

  /**
   * Get uploads that are pending or uploading
   * @returns {MiaUploadTask[]}
   */
  get pendingUploads() {
    return Object.values(state.uploads).filter(
      (u) =>
        u.status === MiaUploadStatus.PENDING ||
        u.status === MiaUploadStatus.UPLOADING
    );
  },

  /**
   * Get pending uploads for a specific feature type
   * @param {string} featureType
   * @returns {MiaUploadTask[]}
   */
  pendingUploadsForFeature(featureType) {
    return Object.values(state.uploads).filter(
      (u) =>
        u.featureType === featureType &&
        (u.status === MiaUploadStatus.PENDING ||
          u.status === MiaUploadStatus.UPLOADING)
    );
  },

  /**
   * Check if there are any active uploads
   * @returns {boolean}
   */
  get hasActiveUploads() {
    return this.activeUploads.length > 0;
  },

  /**
   * Get a specific upload by ID
   * @param {string} id
   * @returns {MiaUploadTask|null}
   */
  getUpload(id) {
    return state.uploads[id] || null;
  },

  /**
   * Start a new upload
   * @param {File} file
   * @param {string} featureType
   * @param {Object} extraData
   * @returns {Promise<string>} upload task ID
   */
  async startUpload(file, featureType, extraData = {}) {
    const id = Date.now().toString();
    const fileName = file.name;

    const task = new MiaUploadTask({
      id,
      file,
      fileName,
      featureType,
      status: MiaUploadStatus.PENDING,
      extraData,
    });

    state.uploads[id] = task;
    notifyListeners();

    // Start the upload in background
    performUpload(id);

    return id;
  },

  /**
   * Cancel an upload
   * @param {string} id
   */
  cancelUpload(id) {
    const task = state.uploads[id];
    if (!task) return;

    task.isCancelled = true;
    if (task.abortController) {
      task.abortController.abort();
    }

    state.uploads[id] = task.copyWith({
      status: MiaUploadStatus.CANCELLED,
      errorMessage: "Upload cancelled by user",
    });
    notifyListeners();
  },

  /**
   * Retry a failed upload
   * @param {string} id
   */
  async retryUpload(id) {
    const task = state.uploads[id];
    if (!task) return;

    if (
      task.status !== MiaUploadStatus.FAILED &&
      task.status !== MiaUploadStatus.CANCELLED
    ) {
      return;
    }

    task.isCancelled = false;
    state.uploads[id] = task.copyWith({
      status: MiaUploadStatus.PENDING,
      uploadedBytes: 0,
      totalBytes: 0,
      errorMessage: null,
    });
    notifyListeners();

    await performUpload(id);
  },

  /**
   * Remove an upload from the list
   * @param {string} id
   */
  removeUpload(id) {
    const task = state.uploads[id];
    if (task && task.status === MiaUploadStatus.UPLOADING) {
      task.isCancelled = true;
      if (task.abortController) {
        task.abortController.abort();
      }
    }
    delete state.uploads[id];
    notifyListeners();
  },

  /**
   * Clear all completed/failed/cancelled uploads
   */
  clearFinishedUploads() {
    Object.keys(state.uploads).forEach((id) => {
      const task = state.uploads[id];
      if (
        task.status === MiaUploadStatus.COMPLETED ||
        task.status === MiaUploadStatus.FAILED ||
        task.status === MiaUploadStatus.CANCELLED
      ) {
        delete state.uploads[id];
      }
    });
    notifyListeners();
  },

  /**
   * Clear finished uploads for a specific feature
   * @param {string} featureType
   */
  clearFinishedUploadsForFeature(featureType) {
    Object.keys(state.uploads).forEach((id) => {
      const task = state.uploads[id];
      if (
        task.featureType === featureType &&
        (task.status === MiaUploadStatus.COMPLETED ||
          task.status === MiaUploadStatus.FAILED ||
          task.status === MiaUploadStatus.CANCELLED)
      ) {
        delete state.uploads[id];
      }
    });
    notifyListeners();
  },

  /**
   * Add a listener for state changes
   * @param {Function} listener
   */
  addListener(listener) {
    if (!state.listeners.includes(listener)) {
      state.listeners.push(listener);
    }
  },

  /**
   * Remove a listener
   * @param {Function} listener
   */
  removeListener(listener) {
    const index = state.listeners.indexOf(listener);
    if (index > -1) {
      state.listeners.splice(index, 1);
    }
  },

  /**
   * Get reactive state (for Vue components)
   */
  get state() {
    return readonly(state);
  },
};

export { MiaUploadTask };
